import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { api } from "../Apis/Api";
import LoadingComponent from "../components/ui/LoadingComponent";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import PaginationControls from "../components/ui/PaginationControls";
import CustomSelect from "../components/common/CustomSelect";
import useDebounce from "../hook/useDebounce";
import Breadcrumb from "../components/Breadcrumb";
import { usePermissions } from "../context/PermissionContext";
import CardContainer from "../components/ui/CardContainer";
import ErrorAlert from "../components/ui/ErrorAlert";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import SortIcons from "../components/ui/SortIcons";
import StatusBadge from "../components/ui/StatusBadge";
import { TextWrap } from "../utils/textWrap";
import { AddButton } from "../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import InfoModal from "../components/ui/InfoeModal";

// Validation Schema
const validationSchema = Yup.object().shape({
  type_id: Yup.mixed()
    .test("type_id", "Vehicle type is required", (value, context) => {
      if (context.parent.modalType === "add") {
        return Array.isArray(value) && value.length > 0;
      }
      return value && value.value !== undefined;
    })
    .required("Vehicle type is required")
    .nullable(),
  hourly_rate: Yup.number()
    .typeError("Hourly rate must be a number")
    .required("Hourly rate is required")
    .positive("Hourly rate must be positive"),
  effective_from: Yup.string()
    .required("Effective from date is required")
    .matches(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
  is_active: Yup.boolean().required("Status is required"),
});

const defaultFormValues = {
  type_id: null,
  hourly_rate: "",
  effective_from: "",
  is_active: true,
  modalType: "",
};

const PricingModelPage = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [pricingId, setPricingId] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [filteredVehicleTypes, setFilteredVehicleTypes] = useState([]);
  const [vehicleTypesLoading, setVehicleTypesLoading] = useState(false);
  const [vehicleTypesSearchTerm, setVehicleTypesSearchTerm] = useState("");
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const debouncedVehicleTypesSearchTerm = useDebounce(
    vehicleTypesSearchTerm,
    500
  );
  const searchInputRef = useRef(null);
  const { hasPermission } = usePermissions();
  const planLocation = useLocation();
  const navigationSuccess = planLocation.state?.progressBarNavigate;

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultFormValues,
  });

  const fetchVehicleTypes = useCallback(async () => {
    try {
      setVehicleTypesLoading(true);
      const res = await api.get("/get-all-vehicle-types", {
        params: { want_pagination: 0, sort_by: "type_name", sort_order: "asc" },
      });
      const { data } = res.data;
      if (!res.data?.success || !Array.isArray(data)) {
        throw new Error("Failed to load vehicle types");
      }
      const typeOptions = [
        { label: "All", value: "all" },
        ...data.map((t) => ({ label: t.type_name, value: t.type_id })),
      ];
      setVehicleTypes(typeOptions);
      setFilteredVehicleTypes(typeOptions);

      const handleRes = res.data;
      handleApiResponse(handleRes, "success", "GET");
    } catch (error) {
      setError("Unable to fetch vehicle types");
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
    } finally {
      setVehicleTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (vehicleTypes.length === 0) fetchVehicleTypes();
  }, [fetchVehicleTypes]);

  useEffect(() => {
    let filtered = vehicleTypes.filter((type) =>
      type.label
        .toLowerCase()
        .includes(debouncedVehicleTypesSearchTerm.toLowerCase())
    );

    if (modalType === "add") {
      const selectedValues =
        getValues("type_id")?.map((item) => item.value) || [];
      if (selectedValues.includes("all")) {
        filtered = [{ label: "All", value: "all" }];
      } else {
        filtered = filtered.filter(
          (type) => !selectedValues.includes(type.value) || type.value === "all"
        );
      }
    } else if (modalType === "edit") {
      filtered = filtered.filter((type) => type.value !== "all");
    }

    setFilteredVehicleTypes(filtered);
  }, [debouncedVehicleTypesSearchTerm, vehicleTypes, modalType, getValues]);

  const fetchPricing = useCallback(
    async (page, perPage, search, sortBy, sortOrder) => {
      setLoading(true);
      setError(null);
      try {
        const fieldMapping = {
          vehicle_type: "vehicle_type",
          hourly_rate: "hourly_rate",
          effective_from: "effective_from",
          is_active: "is_active",
        };

        const queryParams = new URLSearchParams({
          page,
          per_page: perPage,
          ...(search && { search: search }),
          ...(sortBy && { sort_by: fieldMapping[sortBy] || sortBy }),
          ...(sortOrder && { sort_order: sortOrder }),
        });
        const response = await api.get(
          `/get-all-vehicle-pricing?${queryParams.toString()}`
        );
        if (response?.data?.success) {
          const { data = [], pagination = {} } = response.data;
          const mappedPricing = data.map((item, index) => ({
            ...item,
            id: item.pricing_id || index + 1,
            vehicle_type: item.vehicle_type || "-",
            hourly_rate: item.hourly_rate || "-",
            effective_from: item.effective_from || "-", // Store original YYYY-MM-DD format
            display_effective_from: item.effective_from
              ? new Date(item.effective_from).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "-", // Formatted for display
            is_active: item.is_active !== undefined ? item.is_active : false,
          }));
          setPricing(mappedPricing);
          setPagination({
            currentPage: parseInt(pagination.current_page || page, 10),
            perPage: parseInt(pagination.per_page || perPage, 10),
            total: pagination.total || 0,
          });

          const handleRes = response.data;
          handleApiResponse(handleRes, "success", "GET");
        } else {
          throw new Error("Vehicle pricing not found");
        }
      } catch (error) {
        const errorMessage = error.message || "Unable to fetch pricing";
        setError(errorMessage);
        setPricing([]);
        setPagination((prev) => ({ ...prev, total: 0, currentPage: 1 }));
        const handleErr = error.response?.data;
        handleApiResponse(handleErr, "Err", "GET");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    fetchPricing(
      pagination.currentPage,
      pagination.perPage,
      debouncedSearchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearchQuery,
    sortConfig.field,
    sortConfig.direction,
    fetchPricing,
  ]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = useCallback((field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const sortedPricing = useMemo(() => {
    return [...pricing].sort((a, b) => {
      if (!sortConfig.field) return 0;
      let valueA = a[sortConfig.field] ?? "";
      let valueB = b[sortConfig.field] ?? "";
      if (sortConfig.field === "hourly_rate") {
        valueA = valueA !== "-" ? Number(valueA) : -Infinity;
        valueB = valueB !== "-" ? Number(valueB) : -Infinity;
        return sortConfig.direction === "asc"
          ? valueA - valueB
          : valueB - valueA;
      }
      if (sortConfig.field === "effective_from") {
        const dateA = valueA !== "-" ? new Date(valueA) : new Date(0);
        const dateB = valueB !== "-" ? new Date(valueB) : new Date(0);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }
      if (sortConfig.field === "is_active") {
        valueA = valueA ? "Active" : "Inactive";
        valueB = valueB ? "Active" : "Inactive";
      }
      return sortConfig.direction === "asc"
        ? valueA.toString().localeCompare(valueB.toString(), undefined, {
            sensitivity: "base",
          })
        : valueB.toString().localeCompare(valueA.toString(), undefined, {
            sensitivity: "base",
          });
    });
  }, [pricing, sortConfig]);

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const handleOpenModal = (type, pricing = null) => {
    setModalType(type);
    setShowModal(true);
    setPricingId(null);
    setSelectedPricing(null);
    reset({ ...defaultFormValues, modalType: type });

    if (type === "edit" && pricing) {
      if (!pricing.pricing_id) {
        handleCustomResponse("error", "Invalid Pricing ID");
        return;
      }
      setSelectedPricing(pricing);
      setPricingId(pricing.pricing_id);
      setValue("type_id", {
        value: pricing.type_id,
        label: pricing.vehicle_type || "Unknown",
      });
      setValue("hourly_rate", pricing.hourly_rate || "");
      setValue("effective_from", pricing.effective_from || "");
      setValue("is_active", pricing.is_active ?? true);
      setValue("modalType", type);
    } else if (type === "delete" && pricing) {
      if (!pricing.pricing_id) {
        handleCustomResponse("error", "Invalid Pricing ID");
        return;
      }
      setSelectedPricing(pricing);
      setPricingId(pricing.pricing_id);
    } else {
      setValue("type_id", []);
      setValue("effective_from", getCurrentDate());
      setValue("modalType", type);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setPricingId(null);
    setSelectedPricing(null);
    reset({ ...defaultFormValues, modalType: "" });
  };

  const handleAddPricing = async (data) => {
    try {
      setLoading(true);
      if (!data.type_id?.length) {
        handleCustomResponse(
          "error",
          "Please select at least one vehicle type"
        );
        return;
      }
      let typeIds = [];
      if (data.type_id.some((item) => item.value === "all")) {
        typeIds = vehicleTypes
          .filter((type) => type.value !== "all")
          .map((type) => ({ value: type.value, label: type.label }));
      } else {
        typeIds = data.type_id;
      }
      const payload = {
        type_id: typeIds.map((item) => item.value),
        hourly_rate: data.hourly_rate,
        effective_from: data.effective_from,
        is_active: data.is_active,
      };
      const response = await api.post("/add-vehicle-pricing", payload);
      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "POST");
        await fetchPricing(
          pagination.currentPage,
          pagination.perPage,
          searchQuery
        );
        handleCloseModal();
        if (navigationSuccess) {
          navigate(-1);
        }
      } else {
        handleCustomResponse("error", "Failed to add pricing");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPricing = async (data) => {
    if (!pricingId) {
      handleCustomResponse("error", "Invalid Pricing ID");
      return;
    }
    try {
      setLoading(true);
      if (!data.type_id?.value) {
        handleCustomResponse("error", "Please select a vehicle type");
        return;
      }
      const payload = {
        type_id: data.type_id.value,
        hourly_rate: data.hourly_rate,
        effective_from: data.effective_from,
        is_active: data.is_active,
      };
      const response = await api.put(
        `/update-vehicle-pricing/${pricingId}`,
        payload
      );
      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "PUT");
        await fetchPricing(
          pagination.currentPage,
          pagination.perPage,
          searchQuery
        );
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to update pricing");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "PUT");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePricing = async () => {
    if (!pricingId) {
      handleCustomResponse("error", "Invalid Pricing ID");
      return;
    }
    try {
      setLoading(true);
      const response = await api.delete(`/delete-vehicle-pricing/${pricingId}`);
      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "DELETE");
        await fetchPricing(
          pagination.currentPage,
          pagination.perPage,
          searchQuery
        );
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to delete pricing");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "DELETE");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (data) => {
    if (modalType === "add") handleAddPricing(data);
    else if (modalType === "edit") handleEditPricing(data);
    else if (modalType === "delete") handleDeletePricing();
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                placeholder="Search by vehicle type..."
                value={searchQuery}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search by vehicle type"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={(value) =>
              setPagination((prev) => ({
                ...prev,
                perPage: value,
                currentPage: 1,
              }))
            }
          />
          <div className="d-flex align-items-center gap-2">
            {" "}
            {/* 👈 UPDATED: Added Info Button */}
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Vehicle Pricing Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Add New Pricing"
              permissionKey="add_vehicle_pricing"
              onClick={() => handleOpenModal("add")}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <ErrorAlert
        error={error}
        onRetry={() =>
          fetchPricing(pagination.currentPage, pagination.perPage, searchQuery)
        }
      />
    );
  }

  return (
    <>
      <Breadcrumb title="Vehicle Pricing" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={(page) =>
              setPagination((prev) => ({ ...prev, currentPage: page }))
            }
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {sortedPricing.length === 0 && !loading ? (
            <TableDataNoAvailable message="No vehicle pricing found." />
          ) : (
            <table
              className="table bordered-table sm-table mb-0 table-hover"
              aria-label="Vehicle pricing table"
            >
              <thead>
                <tr>
                  <th>S.No</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("vehicle_type")}
                    role="button"
                    aria-label={`Sort by vehicle type, currently sorted ${
                      sortConfig.field === "vehicle_type"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Vehicle Type
                      <SortIcons field="vehicle_type" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("hourly_rate")}
                    role="button"
                    aria-label={`Sort by hourly rate, currently sorted ${
                      sortConfig.field === "hourly_rate"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Hourly Rate
                      <SortIcons field="hourly_rate" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("effective_from")}
                    role="button"
                    aria-label={`Sort by effective from, currently sorted ${
                      sortConfig.field === "effective_from"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Effective From
                      <SortIcons
                        field="effective_from"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer text-center"
                    onClick={() => handleSort("is_active")}
                    role="button"
                    aria-label={`Sort by status, currently sorted ${
                      sortConfig.field === "is_active"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex">
                      Status
                      <SortIcons field="is_active" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPricing.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>
                      {(pagination.currentPage - 1) * pagination.perPage +
                        index +
                        1}
                    </td>
                    <TextWrap location={item.vehicle_type} />
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {item.hourly_rate}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {item.display_effective_from}
                      </span>
                    </td>
                    <td className="">
                      <StatusBadge
                        status={item.is_active ? "Active" : "Inactive"}
                      />
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("update_vehicle_pricing") && (
                          <button
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => handleOpenModal("edit", item)}
                            title={`Edit pricing for ${item.vehicle_type}`}
                            aria-label={`Edit pricing for ${
                              item.vehicle_type || "-"
                            }`}
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {hasPermission("delete_vehicle_pricing") && (
                          <button
                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => handleOpenModal("delete", item)}
                            title={`Delete pricing for ${item.vehicle_type}`}
                            aria-label={`Delete pricing for ${
                              item.vehicle_type || "-"
                            }`}
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 EXISTING: Main Modal (Add/Edit/Delete) */}
      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{
          display: showModal ? "block" : "none",
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalType === "add"
                  ? "Add New Pricing"
                  : modalType === "edit"
                  ? "Edit Pricing"
                  : "Confirm Deletion"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {modalType === "delete" ? (
                <p className="text-neutral-900">
                  Are you sure you want to delete the pricing for{" "}
                  <strong>
                    {selectedPricing?.vehicle_type || "this vehicle type"}
                  </strong>
                  ?
                </p>
              ) : (
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                  <div className="mb-3">
                    <label className="form-label">Vehicle Type *</label>
                    <Controller
                      name="type_id"
                      control={control}
                      render={({ field }) => (
                        <CustomSelect
                          name="type_id"
                          value={field.value}
                          onChange={(selected) => {
                            if (modalType === "add") {
                              if (
                                Array.isArray(selected) &&
                                selected.some((item) => item.value === "all")
                              ) {
                                field.onChange([
                                  { label: "All", value: "all" },
                                ]);
                              } else {
                                field.onChange(selected || []);
                              }
                            } else {
                              field.onChange(selected || null);
                            }
                          }}
                          options={filteredVehicleTypes}
                          placeholder="Select vehicle types"
                          isLoading={vehicleTypesLoading}
                          isSearchable={true}
                          onInputChange={setVehicleTypesSearchTerm}
                          isMulti={modalType === "add"}
                          isDisabled={
                            modalType === "add" &&
                            field.value?.some((item) => item.value === "all")
                          }
                        />
                      )}
                    />
                    {errors.type_id && (
                      <span className="text-danger">
                        {errors.type_id.message}
                      </span>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="hourly_rate" className="form-label">
                      Hourly Rate *
                    </label>
                    <input
                      type="number"
                      {...register("hourly_rate")}
                      className="form-control"
                      id="hourly_rate"
                      placeholder="Enter Hourly Rate"
                      aria-label="Hourly rate"
                    />
                    {errors.hourly_rate && (
                      <span className="text-danger">
                        {errors.hourly_rate.message}
                      </span>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="effective_from" className="form-label">
                      Effective From *
                    </label>
                    <input
                      type="date"
                      {...register("effective_from")}
                      className="form-control"
                      id="effective_from"
                      min={getCurrentDate()}
                      aria-label="Effective from date"
                    />
                    {errors.effective_from && (
                      <span className="text-danger">
                        {errors.effective_from.message}
                      </span>
                    )}
                  </div>
                  {modalType === "edit" && (
                    <div className="mb-3">
                      <label htmlFor="is_active" className="form-label">
                        Status *
                      </label>
                      <Controller
                        name="is_active"
                        control={control}
                        render={({ field }) => (
                          <CustomSelect
                            name="is_active"
                            value={field.value}
                            onChange={(selected) =>
                              field.onChange(selected?.value)
                            }
                            options={[
                              { label: "Active", value: true },
                              { label: "Inactive", value: false },
                            ]}
                            placeholder="Select status"
                            isSearchable={false}
                            isClearable={false}
                          />
                        )}
                      />
                      {errors.is_active && (
                        <span className="text-danger">
                          {errors.is_active.message}
                        </span>
                      )}
                    </div>
                  )}
                </form>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
                aria-label="Cancel"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${
                  modalType === "delete" ? "btn-danger" : "btn-primary-600"
                } d-flex align-items-center`}
                onClick={
                  modalType === "delete"
                    ? handleFormSubmit
                    : handleSubmit(handleFormSubmit)
                }
                disabled={loading}
                aria-label={
                  modalType === "add"
                    ? "Add Pricing"
                    : modalType === "edit"
                    ? "Update Pricing"
                    : "Delete Pricing"
                }
              >
                {loading
                  ? "Please wait..."
                  : modalType === "add"
                  ? "Add Pricing"
                  : modalType === "edit"
                  ? "Update Pricing"
                  : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showModal && <div className="no-modal fade show"></div>}

      {/* 👈 NEW: Info Modal - EXACTLY SAME AS Previous Pages */}

      <InfoModal
        isOpen={showInfoModal}
        onClose={handleInfoClose}
        title="Vehicle Pricing Management - Complete Overview"
      >
        <p className="text-neutral-900 mb-4">
          This page is the <strong>central hub</strong> for managing{" "}
          <strong>hourly rates</strong> for all vehicle types. You can view,
          set, update, or remove pricing for individual vehicle types or apply a
          default rate for all.
        </p>

        <h6 className="fw-bold text-primary-600 mb-3">
          What You See in the List
        </h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Vehicle Type</strong> – e.g., Sedan, SUV, Hatchback, Luxury,
            etc.
          </li>
          <li className="list-group-item">
            <strong>Hourly Rate</strong> – Price per hour for parking/usage
          </li>
          <li className="list-group-item">
            <strong>Status</strong> – Whether this pricing is Active or Inactive
          </li>
          <li className="list-group-item">
            <strong>Default Rate</strong> – Shown clearly if applied to "All
            Vehicles"
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">
          Add New Pricing – Two Options Available
        </h6>
        <p className="small text-neutral-600 mb-2">
          Click "Add Pricing" to choose one of the following:
        </p>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Set Rate for Specific Vehicle Type</strong>
            <br />
            Choose one vehicle type (e.g., SUV) → Enter custom hourly rate
          </li>
          <li className="list-group-item">
            <strong>Set Default Rate for All Vehicles</strong>
            <br />
            Applies the same hourly rate to every vehicle type (overrides
            individual rates)
          </li>
          <li className="list-group-item">
            <strong>Hourly Rate</strong> – Amount in your currency (e.g., 15.00)
          </li>
          <li className="list-group-item">
            <strong>Currency</strong> – Automatically uses system currency
          </li>
          <li className="list-group-item">
            <strong>Status</strong> – Active by default
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">
          Two Action Buttons (Per Pricing Rule)
        </h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Edit (Green Pencil)</strong>
            <br />
            Change the hourly rate or status anytime
          </li>
          <li className="list-group-item">
            <strong>Delete (Red Trash)</strong>
            <br />
            Remove custom pricing (falls back to default rate if exists)
          </li>
        </ul>

        <div className="alert alert-info small mt-4">
          <strong>Note:</strong>
          <br />• If a <strong>Default Rate</strong> is set, it applies to all
          vehicle types unless overridden
          <br />
          • Deleting a specific vehicle type pricing reverts it to the default
          rate
          <br />
          • Only one "Default for All" rule can exist at a time
          <br />• Pricing changes take effect immediately across the system
        </div>

        <p className="text-neutral-900 mt-4 fw-bold text-primary">
          Pro Tip: Set a fair default rate first → Then customize high-value
          vehicles (SUV, Luxury) for better revenue!
        </p>
        <p className="text-neutral-900 small">
          Business Impact: Smart pricing = Higher revenue per hour + Better
          customer segmentation + Maximum parking profitability.
        </p>
      </InfoModal>
    </>
  );
};

export default PricingModelPage;
